import { useCallback, useMemo, useRef, useState, type ReactElement } from "react";

import { useTheme } from "@mui/material/styles";
import {
  PaymentMethod,
  Utils,
  getPaypalCredentialsFromCountry,
  getStripeCustomPaymentMethodIdFromCountry,
  getStripeFrontendApiKeyFromCountry,
  StripePaymentMethod,
  isBankDebitPaymentMethod,
} from "@simplyk/common";
import { StripeElementsOptions } from "@stripe/stripe-js";
import { FieldValues } from "react-hook-form";

import type { PaymentErrorContextType } from "../PaymentElement/PaymentErrorContext";

import { StripePaymentComponentProps } from "./StripePaymentComponentProps";
import { StripePaymentContent } from "./StripePaymentContent";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { ExpressCheckoutWallet } from "@/common/contexts/StripeContext";
import {
  getStripeElementsOnBehalfOf,
  getStripeElementsSetupFutureUsage,
  isPaymentMethodWithStripe,
  STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD,
  STRIPE_PUBLIC_KEYS,
} from "@/common/helpers/stripe";
import { useSupportsExpressCheckout } from "@/common/hooks/useSupportsExpressCheckout";
import { useCheckoutStripe } from "@/common/hooks/useCheckoutStripe";
import {
  PAYPAL_CLIENT_IDS,
  PAYPAL_CUSTOM_PAYMENT_METHOD_IDS,
  VENMO_CUSTOM_PAYMENT_METHOD_IDS,
} from "@/common/providers/StripeProvider";
import { getStripeAppearance } from "@/forms-design-system/FormPaymentMethod/FormPaymentMethodTokens";

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useStripePaymentViewModel<T extends FieldValues>(props: StripePaymentComponentProps<T>) {
  const {
    amount,
    currency,
    country,
    organization,
    formType,
    category,
    formData,
    commandId,
    formAmplitudeContext,
    tip,
    isEmbed,
    stripeRecurrenceInterval,
    paymentMode = "payment",
    prioritizeBankPayment = false,
    isPayPalEnabled = false,
    isFreeTicketing = false,
    tipConfig,
    allowedPaymentMethods: allowedPaymentMethodsProp,
    isPreviewTemplateMode = false,
    isOnboarding = false,
    isPaymentDisabled = false,
    offlinePaymentWording,
    chequeDescription,
    onPayPalSubmit: _onPayPalSubmit,
    onPaymentError,
    paymentErrorState: _paymentErrorState,
    selectedPaymentMethod: controlledSelected,
    setSelectedPaymentMethod: controlledSetSelected,
    setIsSubmitting,
    setIsPadLoading,
    formPathExpressCheckout,
    nestedInFormStripeTree,
  } = props;

  const theme = useTheme();
  const { locale } = useLocaleContext();

  const defaultEmptyAllowedMethods = useRef<PaymentMethod[]>([]);
  const allowedPaymentMethods = allowedPaymentMethodsProp ?? defaultEmptyAllowedMethods.current;

  // Not `FrontendFormContext` — local Safari/setup gating. When `formPathExpressCheckout` is set (Form V2), those values win.
  const internalExpress = useSupportsExpressCheckout(paymentMode);
  const expressCheckoutDisabled =
    formPathExpressCheckout?.expressCheckoutDisabled ?? internalExpress.expressCheckoutDisabled;
  const isExpressCheckoutLoading =
    formPathExpressCheckout?.isExpressCheckoutLoading ?? internalExpress.isExpressCheckoutLoading;
  const supportsExpressCheckout =
    formPathExpressCheckout?.supportsExpressCheckout ?? internalExpress.supportsExpressCheckout;
  const handleSetSupportsExpressCheckout =
    formPathExpressCheckout?.handleSetSupportsExpressCheckout ?? internalExpress.handleSetSupportsExpressCheckout;

  const applePayOrGooglePayAllowed = useMemo(
    () => allowedPaymentMethods.includes(PaymentMethod.ApplePayOrGooglePay),
    [allowedPaymentMethods]
  );

  const displayExpressCheckout =
    formPathExpressCheckout?.displayExpressCheckout ??
    (applePayOrGooglePayAllowed && !isFreeTicketing && (supportsExpressCheckout || isExpressCheckoutLoading));

  const [internalSelected, setInternalSelected] = useState<{
    paymentMethod: PaymentMethod | undefined;
    complete: boolean;
  }>({
    paymentMethod: expressCheckoutDisabled || isPreviewTemplateMode ? PaymentMethod.Card : undefined,
    complete: false,
  });

  const selectedPaymentMethod = controlledSelected ?? internalSelected.paymentMethod;

  const setSelectedPaymentMethod = useCallback(
    (method: PaymentMethod | undefined, complete?: boolean) => {
      if (controlledSetSelected) {
        controlledSetSelected(method, complete);
      } else {
        setInternalSelected({ paymentMethod: method, complete: complete ?? false });
      }
    },
    [controlledSetSelected]
  );

  const defaultSelectedPaymentMethod =
    expressCheckoutDisabled || isPreviewTemplateMode ? PaymentMethod.Card : undefined;

  const expressCheckoutIsSelected =
    formPathExpressCheckout?.expressCheckoutIsSelected ??
    (supportsExpressCheckout && selectedPaymentMethod === PaymentMethod.ApplePayOrGooglePay);

  const selectedPaymentMethodIsBank = isBankDebitPaymentMethod(selectedPaymentMethod);

  const selectedPaymentMethodIsCard =
    selectedPaymentMethod === PaymentMethod.Card || selectedPaymentMethod === PaymentMethod.ApplePayOrGooglePay;

  const [localExpressWallet, setLocalExpressWallet] = useState<ExpressCheckoutWallet | undefined>(undefined);
  const setExpressCheckoutWallet = nestedInFormStripeTree?.setExpressCheckoutWallet ?? setLocalExpressWallet;

  const stripePaymentMethodTypes: StripePaymentMethod[] = useMemo(
    () =>
      nestedInFormStripeTree?.stripePaymentMethodTypes ??
      allowedPaymentMethods
        .map((pm) => {
          if (!isPaymentMethodWithStripe(pm)) {
            return;
          }
          return STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD[pm];
        })
        .filter(Utils.notEmpty),
    [allowedPaymentMethods, nestedInFormStripeTree]
  );

  const stripePaymentMethods: PaymentMethod[] = useMemo(
    () =>
      nestedInFormStripeTree?.stripePaymentMethods ??
      allowedPaymentMethods.filter((pm) => isPaymentMethodWithStripe(pm)),
    [allowedPaymentMethods, nestedInFormStripeTree]
  );

  const zeffyPaymentMethods = useMemo(
    () =>
      nestedInFormStripeTree?.zeffyPaymentMethods ??
      allowedPaymentMethods
        .filter((pm) => !isPaymentMethodWithStripe(pm) && pm !== PaymentMethod.ApplePayOrGooglePay)
        .filter(Utils.notEmpty),
    [allowedPaymentMethods, nestedInFormStripeTree]
  );

  const hasPad = nestedInFormStripeTree?.hasPad ?? allowedPaymentMethods.includes(PaymentMethod.Pad);
  const hasAch = nestedInFormStripeTree?.hasAch ?? allowedPaymentMethods.includes(PaymentMethod.Ach);

  const [isVenmoSdkEligible, setIsVenmoSdkEligible] = useState(true);
  const paypalClientId = getPaypalCredentialsFromCountry(organization.country, PAYPAL_CLIENT_IDS);
  const isPaypalAvailable = isPayPalEnabled && Boolean(paypalClientId);
  const paypalCustomPaymentMethodId =
    isPaypalAvailable && organization.country
      ? getStripeCustomPaymentMethodIdFromCountry(organization.country, PAYPAL_CUSTOM_PAYMENT_METHOD_IDS)
      : undefined;
  const venmoCustomPaymentMethodId =
    isPaypalAvailable && isVenmoSdkEligible && organization.country
      ? getStripeCustomPaymentMethodIdFromCountry(organization.country, VENMO_CUSTOM_PAYMENT_METHOD_IDS)
      : undefined;

  const onBehalfOf = getStripeElementsOnBehalfOf(selectedPaymentMethod, organization.stripeCustomAccountId);
  const roundedAmount = amount >= 0 ? Math.round(amount) : 100;

  /**
   * Form path (`nestedInFormStripeTree`): parent already wrapped us in `<Elements>` + `StripeContext` — do not
   * `loadStripe` or build `StripeElementsOptions` (unused; `StripePaymentFormPath` only renders `getPaymentContent`).
   */
  const { stripeInstance, stripeLoadError, readyPaymentElement, setReadyPaymentElement } = useCheckoutStripe(
    nestedInFormStripeTree ? null : getStripeFrontendApiKeyFromCountry(country, STRIPE_PUBLIC_KEYS) || "",
    locale
  );
  const { elementsKey, elementsOptions } = useMemo(() => {
    if (nestedInFormStripeTree) {
      return {
        elementsOptions: undefined,
        elementsKey: "",
      };
    }

    const options: StripeElementsOptions = {
      mode: paymentMode,
      ...(paymentMode !== "setup" ? { amount: roundedAmount } : {}),
      currency: currency ?? "usd",
      appearance: getStripeAppearance(theme),
      paymentMethodTypes: stripePaymentMethodTypes,
      paymentMethodOptions: {
        us_bank_account: {
          financial_connections: { permissions: ["payment_method" as const] },
        },
      },
      paymentMethodCreation: "manual" as const,
      setupFutureUsage: getStripeElementsSetupFutureUsage(selectedPaymentMethod, paymentMode),
      // @ts-expect-error Stripe.js supports null to clear onBehalfOf, but its published types omit it.
      onBehalfOf,
      ...(paypalCustomPaymentMethodId || venmoCustomPaymentMethodId
        ? {
            customPaymentMethods: [
              ...(paypalCustomPaymentMethodId
                ? [
                    {
                      id: paypalCustomPaymentMethodId,
                      options: { type: "static" as const },
                    },
                  ]
                : []),
              ...(venmoCustomPaymentMethodId
                ? [
                    {
                      id: venmoCustomPaymentMethodId,
                      options: { type: "static" as const },
                    },
                  ]
                : []),
            ],
          }
        : {}),
    };

    return {
      elementsOptions: options,
      elementsKey: `${locale}-${roundedAmount}-${currency}`,
    };
  }, [
    nestedInFormStripeTree,
    locale,
    paymentMode,
    roundedAmount,
    currency,
    theme,
    stripePaymentMethodTypes,
    selectedPaymentMethod,
    onBehalfOf,
    paypalCustomPaymentMethodId,
    venmoCustomPaymentMethodId,
  ]);

  const stripeContextValue = useMemo(
    () => ({
      stripeLoadError,
      readyPaymentElement,
      setReadyPaymentElement,
      hasPad,
      hasAch,
      stripePaymentMethodTypes,
      stripePaymentMethods,
      zeffyPaymentMethods,
      expressCheckoutWallet: localExpressWallet,
      setExpressCheckoutWallet,
    }),
    [
      stripeLoadError,
      readyPaymentElement,
      setReadyPaymentElement,
      hasPad,
      hasAch,
      stripePaymentMethodTypes,
      stripePaymentMethods,
      zeffyPaymentMethods,
      localExpressWallet,
      setExpressCheckoutWallet,
    ]
  );

  const fees = tipConfig?.fees ?? 0;
  const formattedFees = tipConfig?.formattedFees;
  const updateFreeTipAmount = tipConfig?.updateFreeTipAmount ?? (() => {});

  const getPaymentContent = (paymentErrorState: PaymentErrorContextType): ReactElement => (
    <StripePaymentContent
      {...props}
      isPaymentDisabled={isPaymentDisabled}
      hasPad={hasPad}
      hasAch={hasAch}
      stripePaymentMethods={stripePaymentMethods}
      zeffyPaymentMethods={zeffyPaymentMethods}
      expressCheckoutDisabled={expressCheckoutDisabled}
      isExpressCheckoutLoading={isExpressCheckoutLoading}
      supportsExpressCheckout={supportsExpressCheckout}
      displayExpressCheckout={displayExpressCheckout}
      handleSetSupportsExpressCheckout={handleSetSupportsExpressCheckout}
      prioritizeBankPayment={prioritizeBankPayment}
      selectedPaymentMethod={selectedPaymentMethod}
      setSelectedPaymentMethod={setSelectedPaymentMethod}
      expressCheckoutIsSelected={expressCheckoutIsSelected}
      defaultSelectedPaymentMethod={defaultSelectedPaymentMethod}
      setExpressCheckoutWallet={setExpressCheckoutWallet}
      isPreviewTemplateMode={isPreviewTemplateMode}
      tip={tip}
      selectedPaymentMethodIsBank={selectedPaymentMethodIsBank}
      selectedPaymentMethodIsCard={selectedPaymentMethodIsCard}
      formattedFees={formattedFees}
      fees={fees}
      updateFreeTipAmount={updateFreeTipAmount}
      allowedPaymentMethods={allowedPaymentMethods}
      isOnboarding={isOnboarding}
      organizationCountry={country}
      chequeDescription={chequeDescription}
      offlinePaymentWording={offlinePaymentWording}
      paymentErrorState={paymentErrorState}
    />
  );

  return {
    getPaymentContent,
    stripeContextValue,
    elementsKey,
    stripeInstance,
    elementsOptions,
    isPayPalEnabled: isPaypalAvailable,
    paypalClientId,
    isVenmoSdkEligible,
    setIsVenmoSdkEligible,
    errorHandlerInput: {
      onPaymentError,
      formId: formData.id,
      commandId,
      organization,
      setIsSubmitting,
      setIsPadLoading,
      formAmplitudeContext,
      formType,
      tip,
      selectedPaymentMethod,
      isEmbed,
      stripeRecurrenceInterval,
      currency,
      category,
      prioritizeBankPayment,
      expressCheckoutDisabled,
      paymentMode,
    },
  };
}
